import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ClipboardList, Plus, Loader2, Link as LinkIcon, Trash2, Eye, Download } from 'lucide-react';

const formatDt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const Surveys = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('linked');
  const [linkDialog, setLinkDialog] = useState({ open: false, survey: null, customerId: '' });
  const [customers, setCustomers] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [linkedR, unlinkedR, custR] = await Promise.all([
        api.get('/surveys?unlinked=false'),
        api.get('/surveys?unlinked=true'),
        api.get('/customers?limit=500'),
      ]);
      setSurveys(linkedR.data || []);
      setUnlinked(unlinkedR.data || []);
      const arr = Array.isArray(custR.data) ? custR.data : (custR.data.customers || []);
      setCustomers(arr);
    } catch {
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const link = async () => {
    if (!linkDialog.customerId) return;
    try {
      await api.patch(`/surveys/${linkDialog.survey.id}/link?customer_id=${linkDialog.customerId}`);
      toast.success('Survey linked');
      setLinkDialog({ open: false, survey: null, customerId: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link failed');
    }
  };

  const remove = async (s) => {
    if (!window.confirm('Delete this survey?')) return;
    try {
      await api.delete(`/surveys/${s.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const download = async (s) => {
    try {
      const r = await api.get(`/surveys/${s.id}/download`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Survey_${s.survey_date || ''}_${(s.customer_name || 'unlinked').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const renderRow = (s, isUnlinked) => (
    <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`survey-row-${s.id}`}>
      <td className="px-3 py-2 text-sm">{s.survey_date}</td>
      <td className="px-3 py-2 text-sm">{s.surveyor_name}</td>
      <td className="px-3 py-2 text-sm max-w-[280px] truncate" title={s.project_address}>{s.project_address}</td>
      <td className="px-3 py-2 text-sm">
        {s.customer_name ? (
          <Link to={`/customers/${s.customer_id}`} className="text-blue-600 hover:underline">
            {s.customer_name}
          </Link>
        ) : (
          <Badge className="bg-amber-100 text-amber-700">Unlinked</Badge>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-slate-500">{formatDt(s.created_at)}</td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setViewing(s)} data-testid={`view-survey-${s.id}`}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600" onClick={() => download(s)} data-testid={`download-survey-${s.id}`} title="Download PDF">
            <Download className="w-4 h-4" />
          </Button>
          {isUnlinked && (
            <Button size="sm" variant="outline" onClick={() => setLinkDialog({ open: true, survey: s, customerId: '' })} data-testid={`link-survey-${s.id}`}>
              <LinkIcon className="w-3 h-3 mr-1" /> Link
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(s)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );

  const table = (rows, isUnlinked) => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Surveyor</th>
            <th className="px-3 py-2">Project Address</th>
            <th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Submitted</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No surveys</td></tr>
          ) : rows.map((s) => renderRow(s, isUnlinked))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="surveys-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
              <ClipboardList className="inline w-7 h-7 mr-2 text-blue-600" />
              Site Surveys
            </h1>
            <p className="text-slate-500 mt-1">Solar PV & EESS site surveys. Unlinked submissions are stored in SharePoint until matched to a customer.</p>
          </div>
          <Button onClick={() => navigate('/survey-form')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-survey-btn">
            <Plus className="w-4 h-4 mr-2" /> New Survey
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="linked" data-testid="tab-linked">Linked ({surveys.length})</TabsTrigger>
            <TabsTrigger value="unlinked" data-testid="tab-unlinked">
              Needs Linking {unlinked.length > 0 && <Badge className="ml-2 bg-amber-100 text-amber-700">{unlinked.length}</Badge>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="linked">
            <Card>
              <CardContent className="p-0">
                {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-blue-500" /></div> : table(surveys, false)}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="unlinked">
            <Card>
              <CardContent className="p-0">
                {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-blue-500" /></div> : table(unlinked, true)}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Link dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => setLinkDialog({ ...linkDialog, open })}>
        <DialogContent data-testid="link-dialog">
          <DialogHeader>
            <DialogTitle>Link survey to customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">Survey: <b>{linkDialog.survey?.project_address}</b></p>
            <Select value={linkDialog.customerId} onValueChange={(v) => setLinkDialog({ ...linkDialog, customerId: v })}>
              <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.postcode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, survey: null, customerId: '' })}>Cancel</Button>
            <Button onClick={link} disabled={!linkDialog.customerId} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-link-btn">Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="view-survey-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between w-full">
              <DialogTitle>Survey — {viewing?.project_address}</DialogTitle>
              {viewing && (
                <Button size="sm" onClick={() => download(viewing)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="view-download-btn">
                  <Download className="w-4 h-4 mr-1" /> Download PDF
                </Button>
              )}
            </div>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><b>Surveyor:</b> {viewing.surveyor_name}</div>
                <div><b>Date:</b> {viewing.survey_date}</div>
                <div><b>DB Location:</b> {viewing.distribution_board_location}</div>
                <div><b>Distance from inverter:</b> {viewing.distance_from_inverter}</div>
                <div><b>Additional DB:</b> {viewing.additional_db_required}</div>
                <div><b>Roof tiles:</b> {viewing.roof_tile_type}</div>
                <div><b>Hook stops:</b> {viewing.hook_stops_required}</div>
                <div><b>Roofs:</b> {viewing.num_roofs}</div>
                <div><b>Roof condition:</b> {viewing.roof_condition}</div>
                <div><b>Cable route:</b> {viewing.cable_route}</div>
                <div><b>Cable distance:</b> {viewing.cable_distance}</div>
                <div className="col-span-2"><b>Inverter/Battery location:</b> {viewing.inverter_battery_location}</div>
                <div className="col-span-2"><b>Usage (kWh):</b> {viewing.current_usage_kwh}</div>
                <div className="col-span-2"><b>Cost /kWh:</b> {viewing.cost_per_kwh}</div>
                <div className="col-span-2"><b>Future changes:</b> {viewing.future_changes}</div>
                <div className="col-span-2"><b>Roof obstructions:</b> {viewing.roof_obstructions}</div>
                <div className="col-span-2"><b>Shading:</b> {viewing.shading_description}</div>
                <div className="col-span-2"><b>Notes:</b> {viewing.notes}</div>
              </div>
              {Object.entries(viewing.photos || {}).length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold">Photos (click to enlarge)</h3>
                  {Object.entries(viewing.photos).map(([k, arr]) => (
                    <div key={k}>
                      <div className="text-xs text-slate-500 mb-1">{k.replace(/_/g, ' ')}</div>
                      <div className="flex gap-2 flex-wrap">
                        {arr.map((p) => {
                          const src = `${process.env.REACT_APP_BACKEND_URL}/api/surveys/photo/${p.stored_filename}`;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => setLightbox({ src, filename: p.filename })}
                              className="w-28 h-28 rounded border border-slate-200 hover:border-blue-400 overflow-hidden bg-slate-100 transition"
                              title={p.filename}
                            >
                              <img src={src} alt={p.filename} className="w-full h-full object-cover" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-5xl p-2 bg-black/95 border-0" data-testid="photo-lightbox">
          {lightbox && (
            <div className="flex flex-col items-center">
              <img src={lightbox.src} alt={lightbox.filename} className="max-h-[80vh] w-auto object-contain rounded" />
              <div className="flex items-center justify-between w-full mt-2 px-2 text-white">
                <span className="text-sm truncate">{lightbox.filename}</span>
                <a href={lightbox.src} target="_blank" rel="noreferrer" download={lightbox.filename} className="text-xs px-3 py-1 bg-white/10 hover:bg-white/20 rounded">
                  Open / Download original
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Surveys;
